import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Search, UserPlus, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Department {
  id: number;
  name: string;
  sigla: string | null;
}

interface UserProfile {
  id: string;
  full_name: string | null;
  department_id: number | null;
  position: string | null;
  phone: string | null;
}

interface User {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  full_name: string | null;
  department_id: number | null;
  position: string | null;
  phone: string | null;
  role: string | null;
  department?: {
    name: string;
    sigla: string | null;
  };
}

const Settings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [user, setUser] = useState<any>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  const [deletingUserName, setDeletingUserName] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Estado para o novo usuário
  const [newUser, setNewUser] = useState({
    email: "",
    full_name: "",
    department_id: "",
    position: "",
    phone: "",
    password: "",
    isAdmin: false
  });

  // Form state
  const [fullName, setFullName] = useState("");
  const [departmentId, setDepartmentId] = useState<string>("");
  const [position, setPosition] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    loadUser();
    loadDepartments();
    loadUsers();
  }, []);

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]);

  const loadUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setUser(user);
    }
  };

  const loadDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from("departments")
        .select("id, name, sigla")
        .order("name");

      if (error) throw error;
      setDepartments(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar secretarias",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      if (data) {
        setProfile(data);
        setFullName(data.full_name || "");
        setDepartmentId(data.department_id?.toString() || "");
        setPosition(data.position || "");
        setPhone(data.phone || "");
      }
    } catch (error: any) {
      toast({
        title: "Erro ao carregar perfil",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteUser = async (userId: string) => {
    try {
      setIsDeleting(true);
      
      // 1. Excluir o perfil do usuário
      const { error: profileError } = await supabase
        .from("user_profiles")
        .delete()
        .eq("id", userId);
      
      if (profileError) {
        console.error("Erro ao excluir perfil:", profileError);
        throw profileError;
      }
      
      // 2. Tentar excluir o usuário da autenticação (isso requer permissões de admin)
      try {
        // Tentar usar RPC para excluir o usuário (se existir)
        const { error: deleteError } = await supabase
          .rpc('delete_user', { user_id: userId });
          
        if (deleteError) {
          console.warn("Não foi possível excluir o usuário da autenticação:", deleteError);
          // Não vamos lançar o erro, apenas registrar no console
        }
      } catch (deleteErr) {
        console.warn("Erro ao tentar excluir usuário da autenticação:", deleteErr);
        // Não vamos lançar o erro, apenas registrar no console
      }
      
      // 3. Atualizar a lista de usuários
      setUsers(users.filter(u => u.id !== userId));
      
      // 4. Fechar o diálogo e mostrar mensagem de sucesso
      setDeletingUser(null);
      
      toast({
        title: "Usuário excluído",
        description: `O usuário foi excluído com sucesso.`,
      });
    } catch (error: any) {
      console.error("Erro ao excluir usuário:", error);
      toast({
        title: "Erro ao excluir usuário",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleCreateUser = async () => {
    // Validar campos obrigatórios
    if (!newUser.email || !newUser.password) {
      toast({
        title: "Campos obrigatórios",
        description: "Email e senha são obrigatórios.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setCreatingUser(true);
      
      // 1. Criar o usuário no Auth do Supabase
      // Primeiro, vamos tentar criar o usuário
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: newUser.email,
        password: newUser.password,
        options: {
          data: {
            full_name: newUser.full_name || null,
          },
          // Tentar desabilitar email de confirmação
          emailRedirectTo: window.location.origin,
        },
      });
      
      if (authError) throw authError;
      
      if (!authData.user) {
        throw new Error("Erro ao criar usuário");
      }
      
      // 2. Criar o perfil do usuário
      const profileData = {
        id: authData.user.id,
        email: newUser.email,
        full_name: newUser.full_name || null,
        department_id: newUser.department_id ? parseInt(newUser.department_id) : null,
        position: newUser.position || null,
        phone: newUser.phone || null,
        role: newUser.isAdmin ? "admin" : "user",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      const { error: profileError } = await supabase
        .from("user_profiles")
        .insert(profileData);
      
      if (profileError) {
        // Se falhar ao criar o perfil, tentar excluir o usuário criado
        console.error("Erro ao criar perfil:", profileError);
        throw profileError;
      }
      
      // 3. Tentar confirmar o email do usuário (isso requer permissões de admin)
      try {
        // Executar SQL para confirmar o email (isso só funcionará se o usuário tiver permissões)
        const { error: confirmError } = await supabase
          .rpc('confirm_user_email', { user_email: newUser.email });
          
        if (confirmError) {
          console.warn("Não foi possível confirmar o email automaticamente:", confirmError);
          // Não vamos lançar o erro, apenas registrar no console
        }
      } catch (confirmErr) {
        console.warn("Erro ao tentar confirmar email:", confirmErr);
        // Não vamos lançar o erro, apenas registrar no console
      }
      
      // 3. Adicionar o novo usuário à lista local
      const newUserWithDepartment = {
        ...profileData,
        department: departments.find(d => d.id === (newUser.department_id ? parseInt(newUser.department_id) : null)) || null,
        last_sign_in_at: null
      };
      
      setUsers([...users, newUserWithDepartment as any]);
      
      // 4. Fechar o modal e mostrar mensagem de sucesso
      setNewUserOpen(false);
      
      // 5. Limpar o formulário
      setNewUser({
        email: "",
        full_name: "",
        department_id: "",
        position: "",
        phone: "",
        password: "",
        isAdmin: false
      });
      
      toast({
        title: "Usuário criado",
        description: `O usuário ${newUser.email} foi criado com sucesso.`,
      });
      
      // Mostrar instruções para o primeiro login
      toast({
        title: "Instruções",
        description: `O usuário pode fazer login imediatamente com o email e senha fornecidos.`,
        duration: 5000,
      });
    } catch (error: any) {
      console.error("Erro ao criar usuário:", error);
      toast({
        title: "Erro ao criar usuário",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCreatingUser(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      
      // Buscar todos os usuários autenticados
      const { data: authData, error: authError } = await supabase.auth.getUser();
      
      if (authError) throw authError;
      
      // Verificar se o usuário atual é administrador
      if (authData.user?.email !== "admin@admin.com") {
        throw new Error("Apenas administradores podem visualizar a lista de usuários");
      }
      
      console.log("Iniciando busca de usuários...", new Date().toISOString());
      
      // 1. Buscar todos os perfis de usuários
      const { data: userProfiles, error: userProfilesError } = await supabase
        .from("user_profiles")
        .select("*")
        .order('email');

      if (userProfilesError) {
        console.error("Erro ao buscar user_profiles:", userProfilesError);
        throw userProfilesError;
      }
      
      console.log("Perfis de usuários carregados:", userProfiles);
      
      // 2. Buscar todos os departamentos
      const { data: departments, error: departmentsError } = await supabase
        .from("departments")
        .select("*");
        
      if (departmentsError) {
        console.error("Erro ao buscar departments:", departmentsError);
        throw departmentsError;
      }
      
      console.log("Departamentos carregados:", departments);
      
      // 3. Combinar os dados
      const usersWithDepartments = userProfiles?.map(profile => {
        const userDepartment = departments?.find(dept => dept.id === profile.department_id);
        
        return {
          ...profile,
          department: userDepartment || null
        };
      }) || [];
      
      console.log("Usuários com departamentos:", usersWithDepartments);
      console.log("Total de usuários:", usersWithDepartments.length);
      
      // 4. Verificar se o administrador está na lista
      const adminExists = usersWithDepartments.some(user => 
        user.email === "admin@admin.com" || user.role === "admin"
      );
      
      // Se o administrador não estiver na lista, adicioná-lo
      if (!adminExists && authData.user) {
        const adminDepartment = departments?.find(dept => dept.id === 1) || {
          id: 1,
          name: "Secretaria Municipal de Planejamento e Gestão",
          sigla: "SEPLAG"
        };
        
        usersWithDepartments.push({
          id: authData.user.id,
          email: "admin@admin.com",
          role: "admin",
          full_name: "Administrador Marcos Nogueira",
          department_id: 1,
          position: "Administrador",
          phone: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          last_sign_in_at: authData.user.last_sign_in_at,
          department: adminDepartment
        });
      }
      
      setUsers(usersWithDepartments);
    } catch (error: any) {
      console.error("Erro ao carregar usuários:", error);
      toast({
        title: "Erro ao carregar usuários",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      const profileData = {
        id: user.id,
        full_name: fullName,
        department_id: departmentId ? parseInt(departmentId) : null,
        position,
        phone,
      };

      const { error } = await supabase
        .from("user_profiles")
        .upsert(profileData)
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Sucesso",
      });
      
      // 5. Recarregar a lista de usuários
      loadUsers();
      
      toast({
        title: "Usuário criado",
        description: `O usuário ${newUser.email} foi criado com sucesso.`,
      });
    } catch (error: any) {
      console.error("Erro ao criar usuário:", error);
      toast({
        title: "Erro ao criar usuário",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCreatingUser(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Configurações</h1>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile">Perfil</TabsTrigger>
          <TabsTrigger value="account">Conta</TabsTrigger>
          <TabsTrigger value="users">Usuários</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-4">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={user.user_metadata?.avatar_url} />
                  <AvatarFallback>{fullName?.charAt(0) || user.email?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle>Informações do Perfil</CardTitle>
                  <CardDescription>
                    Atualize suas informações pessoais e profissionais
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Nome Completo</Label>
                  <Input
                    id="fullName"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Seu nome completo"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department">Secretaria</Label>
                  <Select
                    value={departmentId}
                    onValueChange={setDepartmentId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma secretaria" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id.toString()}>
                          {dept.sigla ? `${dept.sigla} - ${dept.name}` : dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="position">Cargo</Label>
                  <Input
                    id="position"
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    placeholder="Seu cargo"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Salvar Alterações
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="account" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informações da Conta</CardTitle>
              <CardDescription>
                Gerencie suas informações de acesso
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    value={user.email}
                    disabled
                    className="bg-muted"
                  />
                  <Button variant="outline" disabled>
                    Verificado
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Este é o email usado para acessar sua conta
                </p>
              </div>

              <div className="space-y-2">
                <Label>Senha</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    type="password"
                    value="••••••••"
                    disabled
                    className="bg-muted"
                  />
                  <Button variant="outline" onClick={() => toast({
                    title: "Em breve",
                    description: "A funcionalidade de alterar senha estará disponível em breve!",
                  })}>
                    Alterar
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Altere sua senha periodicamente para manter sua conta segura
                </p>
              </div>

              <div className="space-y-2">
                <Label>Sessão Atual</Label>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Último acesso</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(user.last_sign_in_at).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <Button 
                    variant="destructive"
                    onClick={async () => {
                      await supabase.auth.signOut();
                      window.location.href = '/login';
                    }}
                  >
                    Sair
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Gerenciamento de Usuários</CardTitle>
                  <CardDescription>
                    Visualize e gerencie todos os usuários cadastrados no sistema
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 text-primary font-medium py-1 px-3 rounded-md">
                    Total: {users.length} usuários
                  </div>
                  <Dialog open={newUserOpen} onOpenChange={setNewUserOpen}>
                    <DialogTrigger asChild>
                      <Button>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Novo Usuário
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>Cadastrar Novo Usuário</DialogTitle>
                        <DialogDescription>
                          Preencha os dados para criar um novo usuário no sistema.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="email" className="text-right">
                            Email
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            value={newUser.email}
                            onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                            className="col-span-3"
                            placeholder="email@exemplo.com"
                            required
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="name" className="text-right">
                            Nome
                          </Label>
                          <Input
                            id="name"
                            value={newUser.full_name}
                            onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                            className="col-span-3"
                            placeholder="Nome completo"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="department" className="text-right">
                            Secretaria
                          </Label>
                          <Select 
                            value={newUser.department_id} 
                            onValueChange={(value) => setNewUser({...newUser, department_id: value})}
                          >
                            <SelectTrigger className="col-span-3">
                              <SelectValue placeholder="Selecione uma secretaria" />
                            </SelectTrigger>
                            <SelectContent>
                              {departments.map((dept) => (
                                <SelectItem key={dept.id} value={dept.id.toString()}>
                                  {dept.sigla ? `${dept.sigla} - ${dept.name}` : dept.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="position" className="text-right">
                            Cargo
                          </Label>
                          <Input
                            id="position"
                            value={newUser.position}
                            onChange={(e) => setNewUser({...newUser, position: e.target.value})}
                            className="col-span-3"
                            placeholder="Cargo ou função"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="phone" className="text-right">
                            Telefone
                          </Label>
                          <Input
                            id="phone"
                            value={newUser.phone}
                            onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                            className="col-span-3"
                            placeholder="(00) 00000-0000"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="password" className="text-right">
                            Senha
                          </Label>
                          <Input
                            id="password"
                            type="password"
                            value={newUser.password}
                            onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                            className="col-span-3"
                            placeholder="Senha para acesso"
                            required
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <div className="text-right">
                            <Label htmlFor="admin">Admin</Label>
                          </div>
                          <div className="flex items-center space-x-2 col-span-3">
                            <Checkbox 
                              id="admin" 
                              checked={newUser.isAdmin}
                              onCheckedChange={(checked) => 
                                setNewUser({...newUser, isAdmin: checked === true})
                              }
                            />
                            <label
                              htmlFor="admin"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Usuário é administrador
                            </label>
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setNewUserOpen(false)}>
                          Cancelar
                        </Button>
                        <Button onClick={handleCreateUser} disabled={creatingUser}>
                          {creatingUser && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Cadastrar
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou email..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent>
              {loadingUsers ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum usuário encontrado.
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Usuário</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Secretaria</TableHead>
                        <TableHead>Cargo</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Último Acesso</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users
                        .filter(user => {
                          if (!searchTerm) return true;
                          
                          const searchLower = searchTerm.toLowerCase();
                          return (
                            (user.full_name && user.full_name.toLowerCase().includes(searchLower)) ||
                            (user.email && user.email.toLowerCase().includes(searchLower)) ||
                            (user.department?.name && user.department.name.toLowerCase().includes(searchLower)) ||
                            (user.department?.sigla && user.department.sigla.toLowerCase().includes(searchLower)) ||
                            (user.position && user.position.toLowerCase().includes(searchLower))
                          );
                        })
                        .map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback>
                                    {(user.full_name || user.email)?.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                                <span>{user.full_name || "Não informado"}</span>
                              </div>
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              {user.department ? 
                                (user.department.sigla && user.department.name ? 
                                  `${user.department.sigla} - ${user.department.name}` : 
                                  user.department.name || user.department.sigla) : 
                                "Não informado"}
                            </TableCell>
                            <TableCell>{user.position || "Não informado"}</TableCell>
                            <TableCell>
                              <Badge variant={user.role === "admin" ? "default" : "outline"}>
                                {user.role === "admin" ? "Administrador" : "Usuário"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {user.last_sign_in_at ? 
                                new Date(user.last_sign_in_at).toLocaleString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 
                                "Nunca acessou"}
                            </TableCell>
                            <TableCell className="text-right">
                              <AlertDialog open={deletingUser === user.id} onOpenChange={(open) => !open && setDeletingUser(null)}>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => {
                                      setDeletingUser(user.id);
                                      setDeletingUserName(user.full_name || user.email);
                                    }}
                                    disabled={user.role === "admin"}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Excluir usuário</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Tem certeza que deseja excluir o usuário {deletingUserName}? Esta ação não pode ser desfeita.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleDeleteUser(user.id);
                                      }}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      disabled={isDeleting}
                                    >
                                      {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                      Excluir
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
